import React, { useContext, useRef, useState } from "react";
import { AppContext } from "src/context";
import {
  Breadcrumb,
  Typography,
  Button,
  Row,
  Col,
  message,
  Form,
  Input,
  Modal,
  Select,
  DatePicker,
  Divider,
  Upload,
  InputNumber,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import lang from "src/lang";
import { SelectType } from "src/modules/hr/types";
import coreFunctions from "src/common/utils/coreFunctions";
import { useParams } from "react-router-dom";
import { ActionType } from "@ant-design/pro-components";
import apiRequest from "src/common/utils/apiRequest";
import TreatmentPlansTable from "./tables/TreatmentPlansTable";

const { Title } = Typography;
const { TextArea } = Input;

const TreatmentPlans: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customerList, setCustomerList] = useState<SelectType[]>([]);
  const [physiotherapistList, setPhysiotherapistList] = useState<SelectType[]>(
    []
  );
  const [treatmentPlanId, setTreatmentPlanId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const actionRef = useRef<ActionType>();
  const [form] = Form.useForm();

  const { id } = useParams();
  const { dispatch } = useContext(AppContext);

  const CORE_FUNCTIONS = new coreFunctions();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setTreatmentPlanId(null);
    form.resetFields();
  };

  const handleOk = async () => {
    setSaving(true);
    try {
      await form.validateFields();

      // Get raw form values
      const formValues = form.getFieldsValue();

      // Format dates properly
      const formattedValues = {
        ...formValues,
        start_date: formValues.start_date
          ? formValues.start_date.format("YYYY-MM-DD")
          : null,
        end_date: formValues.end_date
          ? formValues.end_date.format("YYYY-MM-DD")
          : null,
      };

      const API_REQUEST = new apiRequest(
        "treatment-plans/" + (treatmentPlanId ? "edit" : "add"),
        true
      );
      API_REQUEST.send(
        {
          ...formattedValues,
          id: treatmentPlanId,
          hr_record: id,
        },
        (response: {
          status: string;
          message: string;
          treatment_plan: string;
        }) => {
          if (response && response.status == "success") {
            actionRef.current?.reload();
            message.success(response.message);
            handleCancel();
          } else {
            message.error(response.message);
          }
        }
      );
    } catch (error) {
      console.error("Error saving treatment plan:", error);
      message.error("Failed to save treatment plan details. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
      }}
    >
      <Breadcrumb style={{ marginBottom: "16px" }} separator=">">
        <Breadcrumb.Item>{lang.get("breadcrumbs", ["Home"])}</Breadcrumb.Item>
        <Breadcrumb.Item>
          {lang.get("breadcrumbs", ["Treatment Plans"])}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Title level={2} style={{ marginBottom: "16px" }}>
        {lang.get("treatmentPlans", ["Treatment Plans"])}
      </Title>

      <Row
        gutter={[16, 16]}
        style={{ marginBottom: "16px" }}
        align="middle"
        justify="space-between"
      >
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
            {lang.get("treatmentPlans", ["Add New Treatment Plan"])}
          </Button>
        </Col>
      </Row>

      <TreatmentPlansTable
        form={form}
        setTreatmentPlanId={setTreatmentPlanId}
        actionRef={actionRef}
        setIsModalVisible={setIsModalVisible}
      />

      <Modal
        title={treatmentPlanId ? "Edit Treatment Plan" : "Add Treatment Plan"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        okButtonProps={{ loading: saving }}
        destroyOnClose={true}
        centered
        width={700}
        okText="Save"
        cancelText="Cancel"
      >
        <Divider />
        <Form form={form} layout="vertical">
          {/* All your form items remain the same */}
          {/* ... */}
        </Form>
      </Modal>
    </div>
  );
};

export default TreatmentPlans;
